// Сессия пользователя (Р-10): наши access/refresh JWT (access 30 мин, refresh 30 дней, refresh ОДНОРАЗОВЫЙ).
// - Вход — при КАЖДОМ открытии: POST account/telegram/ с init_data (подпись проверяет сервер); ответ несёт авторитетный
//   набор избранного — его и рисуем. Локальное избранное гостя после входа уходит отдельным POST account/favorites/merge/
//   (src/session/favorites.ts, И-3.3 AC-6).
// - Отказ подписи / просроченный initData (401 invalid_telegram_init_data, telegram_init_data_expired), ручки нет
//   (404/405), открыто без initData — работаем ГОСТЕМ: без экрана ошибки, избранное хранится локально; следующее
//   открытие пробует войти снова. Сеть/5xx — прошлая сессия (если была) остаётся, иначе тоже гость до следующего открытия.
// - Обновление строго одно за раз (single-flight): параллельные 401 ждут одно и то же обновление; заранее — если до
//   истечения < 60 с. Окончательный отказ refresh (400/401/403) — новый вход, не вышел — гость.
// Хранилище — localStorage `football.session` (webview Telegram его сохраняет; приватный режим — до перезапуска).
import { ApiError, apiRequest, setBearerProvider } from '../api/client';
import { rememberAnalyticsUser } from '../analytics/track';
import { rawInitData, startParam } from '../telegram/webapp';

export type SessionKind = 'guest' | 'telegram';
interface Stored { access: string; refresh: string; expiresAt: number }
/** Строка избранного сервера (`GET account/favorites/` и `favorites` ответа входа). */
export type FavoriteRow = { entity_type: string; entity_id: number | string };
export interface TokenResponse {
  access_token: string; refresh_token: string; expires_in?: number;
  /** обезличенный номер для аналитики (не Telegram id) */ analytics_id?: string;
  /** авторитетный набор избранного аккаунта (ключ есть у сборок сервера с §125-D) */
  favorites?: Partial<Record<string, FavoriteRow[]>>;
}
/** Приёмник набора избранного из ответа входа (подключает favorites.ts). */
export interface FavoritesBridge {
  accepted(favorites: TokenResponse['favorites']): void;
}

/** Параметр запуска как есть — сервер запоминает по нему первое касание (рекламную метку); только алфавит Telegram, ≤ 64. */
export const startParamBody = (param: string | undefined): { start_param?: string } =>
  (param && /^[A-Za-z0-9_-]{1,64}$/.test(param) ? { start_param: param } : {});

const KEY = 'football.session';
const ACCOUNT = '/api/v5/mobile/account';
/** Отказ входа, после которого — гость без экрана ошибки. */
const GUEST_CODES = ['invalid_telegram_init_data', 'telegram_init_data_expired'];
let memory: Stored | null = load();
let pending: Promise<Stored | null> | null = null;
let bridge: FavoritesBridge | null = null;
const listeners = new Set<() => void>();

function load(): Stored | null {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Stored | null;
    return s && typeof s.access === 'string' && typeof s.refresh === 'string' ? s : null;
  } catch { return null; }
}
function save(next: Stored | null): void {
  memory = next;
  try { if (next) localStorage.setItem(KEY, JSON.stringify(next)); else localStorage.removeItem(KEY); } catch { /* приватный режим */ }
  listeners.forEach((l) => l());
}
function fromResponse(r: TokenResponse): Stored {
  rememberAnalyticsUser(r.analytics_id);
  return { access: r.access_token, refresh: r.refresh_token, expiresAt: Date.now() + (r.expires_in ?? 15 * 60) * 1000 };
}

export function setFavoritesBridge(next: FavoritesBridge | null): void { bridge = next; }
export function hasSession(): boolean { return memory !== null; }
export function sessionKind(): SessionKind { return memory ? 'telegram' : 'guest'; }
export function onSessionChange(listener: () => void): () => void { listeners.add(listener); return () => listeners.delete(listener); }

/** Окончательный отказ: обновлять бессмысленно — нужна новая сессия. */
export function isFinalRejection(error: unknown): boolean {
  return error instanceof ApiError && error.kind === 'http' && [400, 401, 403].includes(error.status);
}

/** Отказ входа, после которого работаем гостем: подпись не принята, initData просрочен, ручки нет. */
export function isGuestFallback(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.kind !== 'http') return false;
  return error.status === 404 || error.status === 405 || (error.status === 401 && GUEST_CODES.includes(error.code ?? '')) || isFinalRejection(error);
}

/** Вход через Telegram. null — гость (нет initData или отказ подписи); сеть/5xx — пробрасываются. */
async function telegramLogin(): Promise<Stored | null> {
  const initData = rawInitData();
  if (!initData) return null;
  try {
    const r = await apiRequest<TokenResponse>('POST', `${ACCOUNT}/telegram/`, {
      body: { init_data: initData, ...startParamBody(startParam()) },
    });
    const stored = fromResponse(r);
    bridge?.accepted(r.favorites);
    return stored;
  } catch (e) {
    if (isGuestFallback(e)) return null;
    throw e;
  }
}

/** Single-flight: один вход/обновление на всё приложение. null — сессии нет (гость). */
function obtain(work: () => Promise<Stored | null>): Promise<Stored | null> {
  if (!pending) {
    pending = work().then((s) => { save(s); return s; }).finally(() => { pending = null; });
  }
  return pending;
}

async function refresh(current: Stored): Promise<Stored | null> {
  try {
    return fromResponse(await apiRequest<TokenResponse>('POST', `${ACCOUNT}/refresh/`, { body: { refresh_token: current.refresh } }));
  } catch (e) {
    if (isFinalRejection(e)) return telegramLogin();
    throw e;
  }
}

/** Вход при открытии приложения (main.tsx). Никогда не бросает: исход — вид сессии. Сеть/5xx — прошлая сессия
 *  остаётся (её токены ещё годны или обновятся), без неё — гость до следующего открытия. */
export async function startSession(): Promise<SessionKind> {
  try {
    await obtain(telegramLogin);
  } catch { /* сеть / 5xx: в memory — прошлая сессия или ничего */ }
  return sessionKind();
}

/** Нет сессии — гостю Bearer не положен (запросы с auth гость не шлёт). */
export class NoSessionError extends Error { constructor() { super('no_session'); } }

export async function validToken(): Promise<string> {
  const current = pending ? await pending : memory;
  if (!current) throw new NoSessionError();
  if (current.expiresAt - Date.now() < 60_000) {
    const next = await obtain(() => refresh(current));
    if (!next) throw new NoSessionError();
    return next.access;
  }
  return current.access;
}

export function signOut(): void { save(null); }

setBearerProvider({
  has: hasSession,
  token: validToken,
  invalidate(rejected) {
    // протух именно этот токен: следующий validToken() обновит (если обновление уже идёт — дождётся его)
    if (memory && memory.access === rejected) memory = { ...memory, expiresAt: 0 };
  },
});
